/** Centralized help/documentation content for every screen & wizard step.
 *  Pulled into the HelpButton sheet via a `topic` key. */

export const HELP = {
  dashboard: {
    title: "Dashboard",
    subtitle: "Phase 1 · Admin Board",
    intro:
      "The dashboard gives you a quick overview of all sequences you've cut, tracked and labeled, plus a shortcut to start a new one.",
    sections: [
      {
        h: "What is a sequence?",
        p: "A sequence is one captured ultimate-frisbee situation that you cut out of a full match recording — typically one offensive point, one defensive stop, an endzone play, a reset chain or a transition. Every sequence has its own ID, its own clipped MP4 and its own ML-ready tracking data.",
      },
      {
        h: "Stat cards",
        list: [
          "**Sequences** — total entries in the library.",
          "**Tracked** — sequences for which YOLOv8x + ByteTRACK has completed.",
          "**Labeled** — sequences with offense/defense/phase metadata, i.e. fully ML-ready.",
          "**Footage** — total seconds of cut sequence material in your library.",
        ],
      },
      {
        h: "Sequence library",
        p: "The table shows the latest sequences. Click **Open** to resume any sequence at the right wizard step, or delete it if it was a mistake.",
      },
    ],
  },

  step1: {
    title: "Step 1 · Upload & Game Configuration",
    subtitle: "Bring in the full match video and tell us what kind of game it is.",
    intro:
      "Drop in the source recording (a full match or a long clip) and capture the static properties of the game — those that don't change from point to point.",
    sections: [
      {
        h: "What to upload",
        p: "A single .mp4 or .mov file of the match, ideally from a fixed wide-angle camera (sideline or endline). Up to 10 GB, streamed directly to Backblaze in 16 MB chunks. We slice it down to the actual sequence later in Step 3, so it's fine if the file covers a whole game.",
      },
      {
        h: "Match Setup",
        list: [
          "**Format** — Outdoor (100×37 m, 7v7), Beach (75×25 m, 5v5) or Indoor (50×25 m, 5v5). Drives the calibration defaults.",
          "**Division** — Open, Women or Mixed. Stays constant for the whole match.",
          "**Gender Split** is asked **per sequence in Step 4**, not here — because in Mixed the split (e.g. 3M/2W vs 2M/3W) changes from point to point.",
        ],
      },
      {
        h: "After upload",
        p: "We extract metadata (fps, duration, resolution) and store the source in object storage. The wizard advances to field calibration.",
      },
    ],
  },

  step2: {
    title: "Step 2 · Field Calibration",
    subtitle: "4-Point Homography — map pixels to meters.",
    intro:
      "To get real-world (m, km/h) tracking output we need to know how camera pixels map to the field. You click 4 reference points and the backend computes a perspective transform with cv2.getPerspectiveTransform.",
    sections: [
      {
        h: "Endzone vs. Full Field",
        p: "Recommended: **Endzone**. The full pitch is rarely visible in one frame, and the exact field size varies. An endzone is small, almost always fully on screen, and has well-defined goal lines. Switch to **Full Field** only if you really see all 4 outer corners.",
      },
      {
        h: "Seek to the right frame",
        p: "Use the scrub bar below the image to pick a frame where the chosen reference (endzone or full field) is fully and clearly visible. Frame-step arrows (◀ ▶) and −1s / +1s buttons let you fine-tune. Selecting a different frame resets the points.",
      },
      {
        h: "Click order (clockwise)",
        list: [
          "**Endzone mode** · Front-Left → Front-Right → Back-Right → Back-Left. Front edge = goal line.",
          "**Full Field mode** · Top-Left → Top-Right → Bottom-Right → Bottom-Left.",
        ],
      },
      {
        h: "Editable dimensions",
        p: "Defaults come from the chosen format, but pitch sizes vary in real life. Override the Length and Width (in meters) to match the actual painted field if known. These dimensions become the world-space reference for all downstream tracking output.",
      },
    ],
  },

  step3: {
    title: "Step 3 · Sequence Lab",
    subtitle: "Cut multiple sequences from this match, track and label each one.",
    intro:
      "This is the workshop where one uploaded video becomes many ML-ready sequences. Scrub through the match, mark In/Out points to create a new sequence, then track it, label it and export.",
    sections: [
      {
        h: "What is a sequence?",
        p: "One sequence = one tactical situation. Typically one full point or a significant phase: a pull play, a reset chain, an endzone score, a turnover + counter-attack. Keep them focused (max 60 seconds) — short, atomic situations make better training data.",
      },
      {
        h: "How to create a new sequence",
        list: [
          "Scrub the video to the In point (start of the situation).",
          "Click **Set In**.",
          "Scrub forward to the Out point.",
          "Click **Set Out**.",
          "Click **Add Sequence** — a new row appears in the sequence list with status *Draft*.",
        ],
      },
      {
        h: "Track & Label",
        list: [
          "**Track** runs YOLOv8x + ByteTRACK on the cut (~CPU sampling: 5 fps). Progress updates live.",
          "**Label** opens the tactical annotation form (offense, defense, phase, quality, difficulty, gender split for Mixed, tags, expert note).",
          "**Export** downloads MP4 / CSV / JSON for the sequence.",
        ],
      },
      {
        h: "Order doesn't matter",
        p: "You can create all the cuts first, then run all the tracking jobs, then label everything — or do them one at a time. Sequences are independent. Jobs run sequentially on the CPU worker queue.",
      },
    ],
  },

  step4: {
    title: "Step 4 · Tactical Labeling",
    subtitle: "Add the expert annotations that turn a clip into ML training data.",
    intro:
      "Every tracked sequence needs a tactical label set so a downstream model can learn the mapping from player positions → tactical concept.",
    sections: [
      {
        h: "Gender Split (Mixed only)",
        p: "If the match Division is **Mixed**, choose the on-field gender ratio for **this specific sequence**. Mixed splits change from point to point — common Indoor splits are 3M/2W or 2M/3W. Soft Mixed values (4M/1W, 1M/4W) are used in specific rule sets.",
      },
      {
        h: "Offense System",
        list: [
          "**Vertical Stack** — handlers in front, cutters stacked deep through the middle.",
          "**Horizontal Stack** — cutters spread laterally across the field at the same depth.",
          "**Side Stack** — stack pushed to one side of the field for iso/give-go space.",
          "**Iso** — one cutter isolated for a 1-on-1 matchup.",
          "**Split Stack / German** — situational hybrids.",
        ],
      },
      {
        h: "Defense System",
        list: [
          "**Man** — strict person-to-person.",
          "**Zone (Cup / Clam)** — area defense, force passes inside.",
          "**Junk / Switch** — hybrid coverage with switches between defenders.",
          "**Force Flick / Backhand** — direction the mark forces the thrower.",
        ],
      },
      {
        h: "Game Phase, Quality, Difficulty",
        list: [
          "**Game Phase** — Pull Play, Reset, Endzone, Transition, Counter-Attack, Stalled. The tactical context the sequence belongs to.",
          "**Quality** — Low / Medium / High / Reference. Reference = textbook example.",
          "**Difficulty** — how hard the situation was for the offense.",
        ],
      },
      {
        h: "Tags & Expert Note",
        p: "Free-form text and tags (e.g. `huck`, `layout`, `break`, `dump-swing`). Tags become filter chips later in the library; the expert note is preserved verbatim in the JSON export.",
      },
    ],
  },

  step5: {
    title: "Step 5 · Preview & Export",
    subtitle: "Verify the tracking and download the ML-ready bundle.",
    intro:
      "Final verification. You see the original clip with colored player overlays plus a synchronized top-down view; if both look correct, export.",
    sections: [
      {
        h: "What's in the preview",
        list: [
          "**Top panel** — the clipped video with a colored dot per Player_ID at their detected pixel position. Same color across frames = same tracked player.",
          "**Top-down 2D field** — same players, but projected through your homography to real-world field coordinates. Use this view to verify the calibration: positions should look natural on the schematic field.",
        ],
      },
      {
        h: "Exports — ML-ready bundle",
        list: [
          "**`seq_<id>.mp4`** — the cut clip itself, exactly the range you defined in Step 3.",
          "**`seq_<id>.csv`** — flat table with columns Frame, Timestamp_s, Player_ID, Pixel_X/Y, Field_X/Y, Speed_KMH. The canonical training format.",
          "**`seq_<id>.json`** — same tracking data plus full metadata: game config, calibration (mode, dimensions, homography), tactical labels, tags. Self-contained.",
        ],
      },
      {
        h: "Finish",
        p: "Hitting **Finish & Return** flips the sequence status to *exported* and brings you back to the dashboard. Re-opening the sequence later goes straight to this preview.",
      },
    ],
  },

  library: {
    title: "Sequence Library",
    subtitle: "All your sequences, searchable.",
    intro:
      "Every sequence you've ever created lives here, with the most important attributes inline. The library is the dataset you're building.",
    sections: [
      {
        h: "Columns",
        list: [
          "**ID** — short hash of the sequence ID.",
          "**Format** — Outdoor / Beach / Indoor.",
          "**Offense / Defense** — the labeled systems.",
          "**Quality** — Low → Reference.",
          "**Duration** — the cut length in seconds.",
          "**Status** — Draft → Uploaded → Calibrated → Tracking → Tracked → Labeled → Exported.",
        ],
      },
      {
        h: "Actions",
        p: "**Open** resumes the wizard at the correct step. **Delete** removes the sequence including its source video and exports.",
      },
    ],
  },

  sequences_view: {
    title: "All Sequences",
    subtitle: "Flat view of every cut across every match.",
    intro:
      "The Sequences page is your full dataset view. Every clip you've cut from any match shows here with its parent match, cut range, tactical labels and current pipeline status.",
    sections: [
      {
        h: "Pipeline status",
        list: [
          "**Draft** — cut created but not tracked nor labeled.",
          "**Tracking** — YOLOv8x + ByteTRACK currently running.",
          "**Tracked** — tracking complete, waiting for labels.",
          "**Labeled** — labels saved (tracking may still be pending; run it from the row's Track button).",
          "**Exported** — at least one export (MP4/CSV/JSON) downloaded.",
          "**Failed** — tracking failed; click Track again to retry.",
        ],
      },
      {
        h: "Actions per row",
        list: [
          "**Open in Lab** (↗) jumps to the Sequence Lab of the parent match at this clip.",
          "**MP4 · CSV · JSON** download icons appear once tracking has run.",
          "**Trash** deletes the sequence (keeps the parent match).",
        ],
      },
      {
        h: "Search",
        p: "The search box matches sequence name, match name, ID prefix, offense and defense labels — handy for finding all `Vertical Stack` examples or all `Zone (Cup)` defenses.",
      },
    ],
  },

  settings: {
    title: "Settings",
    subtitle: "Pipeline and storage configuration.",
    intro:
      "Read-only view of the active AI pipeline, storage backend and database in Phase 1. Phase 2 will expose tunable parameters (model selection, GPU runners, quotas).",
    sections: [
      {
        h: "AI Pipeline",
        p: "YOLOv8x (detection) + ByteTRACK (multi-object tracking), running on CPU here. The model is hot-swappable via the `YOLO_MODEL` env var, and the sampling rate via `TRACKING_SAMPLE_FPS`.",
      },
      {
        h: "Storage",
        p: "Source videos and tracking exports live in Backblaze B2 (S3-compatible) under the `spiele/`, `sequenzen/` and `datasets/` prefixes. A local on-disk cache speeds up repeated frame requests.",
      },
    ],
  },
};

/** Per-parameter help shown via small `?` icons next to each labeling field. */
export const PARAM_HELP = {
  gender_split: {
    title: "Gender Split",
    text: "On-field gender ratio for this sequence. In Mixed division this changes from point to point — set it per sequence here. `Soft Mixed` values (4M/1W, 1M/4W in 5v5; 5M/2W, 2M/5W in 7v7) are rule-set specific.",
  },
  offense_system: {
    title: "Offense System",
    items: [
      ["Vertical Stack", "Handlers in front; cutters stacked through the middle of the field, attacking the deep & in cuts."],
      ["Horizontal Stack", "Cutters spread laterally at the same depth; gives multiple lanes and easier give-go flow."],
      ["Side Stack", "Stack pushed to one side of the field, isolating space on the open side for iso & continuation cuts."],
      ["Iso", "One cutter isolated for a 1-on-1; rest of the team clears space."],
      ["Split Stack", "Hybrid: stack split into two smaller groups (vert + horiz characteristics)."],
      ["German", "European-style positional setup with strict handler/cutter roles and emphasis on dump-swings."],
    ],
  },
  defense_system: {
    title: "Defense System",
    items: [
      ["Man", "Strict person-to-person coverage."],
      ["Zone (Cup)", "Three-person cup around the disc + wings + deep. Forces short passes."],
      ["Zone (Clam)", "Switching zone variant; defenders cover lanes near the disc."],
      ["Junk", "Hybrid: short-range zone-like coverage merging into man down field."],
      ["Switch", "Man-base with planned switches on cuts and screens."],
      ["Force Flick / Backhand", "Direction the mark forces the thrower (specify only the force direction)."],
    ],
  },
  game_phase: {
    title: "Game Phase",
    items: [
      ["Pull Play", "First touches after the pull — set offense vs. set defense."],
      ["Reset", "Dump/swing reset to refresh stall and reposition the disc."],
      ["Endzone", "Inside ~15 m of the endzone; goal-line offense."],
      ["Transition", "Turnover + immediate switch from D → O or O → D before reset."],
      ["Counter-Attack", "Fast break after a turnover, hitting deep before D resets."],
      ["Stalled", "High stall count, broken structure — often ends in a hammer or turn."],
    ],
  },
  quality: {
    title: "Quality",
    items: [
      ["Low", "Execution errors or unclear tactical intent — not great training data."],
      ["Medium", "Decent execution, useful but not exemplary."],
      ["High", "Clean execution, clear intent — strong training data."],
      ["Reference", "Textbook example — use as a gold-standard demo of the labeled system."],
    ],
  },
  difficulty: {
    title: "Difficulty",
    text: "Subjective rating of how hard the offense's task was given the defense pressure and field position. Helps balance datasets and weight examples in training.",
    items: [
      ["Easy", "Mismatch in offense's favor; lots of open space."],
      ["Medium", "Even matchup; standard tactical execution required."],
      ["Hard", "Heavy defensive pressure, tight windows, low stall."],
    ],
  },
  tags: {
    title: "Tags",
    text: "Free-form keywords to enrich filtering and downstream querying. Common: `huck`, `layout`, `break`, `dump-swing`, `iso-deep`, `flick-io`, `bookend`. Becomes a `tags` array in the JSON export.",
  },
  expert_note: {
    title: "Expert Note",
    text: "Free-form text observation: positioning issues, decision quality, key cuts/errors, throw selection. Preserved verbatim in the JSON export under `meta.sequence.labels.expert_note`.",
  },
};
